'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Hash, EyeOff, Eye } from 'lucide-react';
import AuthGuard from '@/components/AuthGuard';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { useUserProfile } from '@/hooks/useUserProfile';
import { resolveDisplayName } from '@/lib/displayName';
import { Avatar } from '@/components/ds';
import { cn } from '@/lib/utils';
import GroupSettingsModal from '@/components/GroupSettingsModal';
import GroupHeader from '@/components/groups/GroupHeader';
import ChannelsPanel from '@/components/groups/ChannelsPanel';
import RosterRail, { type RosterMember } from '@/components/groups/RosterRail';
import RequestToJoinPanel from '@/components/groups/RequestToJoinPanel';
import GroupComposer, {
  type GroupComposerSendPayload,
} from '@/components/groups/GroupComposer';
import DaySeparator from '@/components/dm/DaySeparator';
import { isSameDay, formatTimeOfDay } from '@/components/dm/dmDate';
import {
  type GroupRole,
  rankOf,
  canHideMessage,
} from '@/lib/groups/authority';
import type { Group } from '@/types';

type ApiMember = {
  userId: string;
  role: string | null;
  mutedUntil?: string | null;
  user: { id: string; name: string; username: string; avatar: string | null } | null;
};

type ApiGroup = {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  memberCount: number;
  isJoined: boolean;
  isAdmin: boolean;
  ownerId: string;
  privacy: 'public' | 'request' | 'private' | string;
  channels: Array<{ id: string; name: string }>;
  members?: ApiMember[];
  banner?: string | null;
  avatar_url?: string | null;
  // Viewer's resolved authority on the group (owner|admin|moderator|member).
  viewerRole?: string | null;
  role?: string | null;
  // Phase 3 request-to-join.
  rules?: string | null;
  viewerRequestStatus?: 'none' | 'pending' | 'approved' | 'denied' | string | null;
};

type ApiMessage = {
  id: string;
  groupId: string;
  channelId: string;
  userId: string;
  user: { name: string; username: string; avatar: string };
  content: string;
  messageType?: 'text' | 'image' | 'video' | 'gif' | 'link' | 'file' | 'embed';
  mediaUrl?: string | null;
  mediaThumbnail?: string | null;
  isDeleted?: boolean;
  hidden?: boolean;
  createdAt: string;
};

const POLL_MS = 4000;

function GroupDetailInner() {
  const params = useParams();
  const router = useRouter();
  const groupId = String(params.id || '');
  const { user } = useAuthContext();
  const { profile: viewerProfile } = useUserProfile();

  const [group, setGroup] = useState<ApiGroup | null>(null);
  const [messages, setMessages] = useState<ApiMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const channels = useMemo(
    () => (group?.channels && group.channels.length ? group.channels : [{ id: 'general', name: 'general' }]),
    [group?.channels],
  );
  const [activeChannelId, setActiveChannelId] = useState('general');
  const activeChannel = channels.find((c) => c.id === activeChannelId) || channels[0];

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const initialScrollDoneRef = useRef(false);

  const viewerRole = (group?.viewerRole ?? group?.role ?? null) as GroupRole | null;
  const viewerRank = rankOf(viewerRole);
  const isModerator = viewerRank >= rankOf('moderator');
  const canManage = viewerRank >= rankOf('admin');

  // Presence is not wired yet — the viewer is the only known-online member.
  const onlineIds = useMemo(
    () => new Set<string>(user?.id ? [user.id] : []),
    [user?.id],
  );

  // role-by-userId map (owner derived from ownerId) for message-author gating.
  const roleByUserId = useMemo(() => {
    const map = new Map<string, GroupRole>();
    if (group) {
      for (const m of group.members || []) {
        map.set(m.userId, m.userId === group.ownerId ? 'owner' : ((m.role as GroupRole) ?? 'member'));
      }
    }
    return map;
  }, [group]);

  const rosterMembers: RosterMember[] = useMemo(
    () =>
      (group?.members || []).map((m) => ({
        userId: m.userId,
        name: m.user?.name || 'Unknown',
        username: m.user?.username?.replace(/^@/, '') || null,
        avatarUrl: m.user?.avatar || null,
        role: (m.role as GroupRole | null) ?? null,
        mutedUntil: m.mutedUntil ?? null,
      })),
    [group?.members],
  );

  const loadGroup = useCallback(async () => {
    try {
      const res = await fetch(`/api/groups/${groupId}${user?.id ? `?userId=${user.id}` : ''}`, {
        cache: 'no-store',
      });
      if (!res.ok) {
        if (res.status === 404) setPageError('Group not found.');
        else setPageError('Failed to load group.');
        return;
      }
      const data = (await res.json()) as ApiGroup;
      setGroup(data);
    } catch (err) {
      console.error('[GroupDetail] load failed:', err);
      setPageError('Failed to load group.');
    }
  }, [groupId, user?.id]);

  const loadMessages = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/groups/${groupId}/messages?channel=${encodeURIComponent(activeChannelId)}&limit=50`,
        { cache: 'no-store' },
      );
      if (!res.ok) return;
      const data = (await res.json()) as ApiMessage[];
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[GroupDetail] messages load failed:', err);
    }
  }, [groupId, activeChannelId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadGroup(), loadMessages()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadGroup, loadMessages]);

  // Lightweight polling fallback. No new realtime channels added in Phase 1.
  useEffect(() => {
    if (!groupId || !group?.isJoined) return;
    const id = setInterval(() => {
      void loadMessages();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [groupId, group?.isJoined, loadMessages]);

  useEffect(() => {
    initialScrollDoneRef.current = false;
    isNearBottomRef.current = true;
  }, [activeChannelId]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || messages.length === 0) return;
    if (!initialScrollDoneRef.current) {
      el.scrollTop = el.scrollHeight;
      initialScrollDoneRef.current = true;
      isNearBottomRef.current = true;
      return;
    }
    if (isNearBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  };

  const handleSend = async (payload: GroupComposerSendPayload) => {
    if (!user || !group) return;
    const res = await fetch(`/api/groups/${groupId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
      body: JSON.stringify({
        channelId: activeChannelId,
        content: payload.content,
        messageType: payload.messageType,
        mediaUrl: payload.mediaUrl,
        mediaThumbnail: payload.mediaThumbnail,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Send failed (${res.status})`);
    }
    const inserted = (await res.json()) as ApiMessage;
    setMessages((prev) => [...prev, inserted]);
  };

  // ── Roster moderation actions — optimistic with rollback on failure.
  // The server endpoints remain the real authority gate; these handlers just
  // reflect the result locally and revert (then rethrow) when the call fails.
  const patchMembers = (updater: (members: ApiMember[]) => ApiMember[]) =>
    setGroup((g) => (g ? { ...g, members: updater(g.members || []) } : g));

  const handleChangeRole = async (userId: string, role: 'admin' | 'moderator' | 'member') => {
    const snapshot = group;
    patchMembers((ms) => ms.map((m) => (m.userId === userId ? { ...m, role } : m)));
    try {
      const res = await fetch(`/api/groups/${groupId}/members/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error();
    } catch (err) {
      setGroup(snapshot);
      throw err;
    }
  };

  const handleMute = async (userId: string, minutes: number | null) => {
    const snapshot = group;
    const mutedUntil =
      typeof minutes === 'number' && minutes > 0
        ? new Date(Date.now() + minutes * 60_000).toISOString()
        : null;
    patchMembers((ms) => ms.map((m) => (m.userId === userId ? { ...m, mutedUntil } : m)));
    try {
      const res = await fetch(`/api/groups/${groupId}/members/${userId}/mute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minutes }),
      });
      if (!res.ok) throw new Error();
    } catch (err) {
      setGroup(snapshot);
      throw err;
    }
  };

  const removeMemberOptimistic = (userId: string) =>
    setGroup((g) =>
      g
        ? {
            ...g,
            members: (g.members || []).filter((m) => m.userId !== userId),
            memberCount: Math.max(0, g.memberCount - 1),
          }
        : g,
    );

  const handleKick = async (userId: string) => {
    const snapshot = group;
    removeMemberOptimistic(userId);
    try {
      const res = await fetch(`/api/groups/${groupId}/members/${userId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
    } catch (err) {
      setGroup(snapshot);
      throw err;
    }
  };

  const handleBan = async (userId: string) => {
    const snapshot = group;
    removeMemberOptimistic(userId);
    try {
      const res = await fetch(`/api/groups/${groupId}/bans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      if (!res.ok) throw new Error();
    } catch (err) {
      setGroup(snapshot);
      throw err;
    }
  };

  const handleHideMessage = async (messageId: string, hidden: boolean) => {
    const snapshot = messages;
    setMessages((ms) =>
      ms.map((m) => (m.id === messageId ? { ...m, isDeleted: hidden, hidden } : m)),
    );
    try {
      const res = await fetch(`/api/groups/${groupId}/messages/${messageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden }),
      });
      if (!res.ok) throw new Error();
    } catch (err) {
      setMessages(snapshot);
      console.error('[GroupDetail] hide toggle failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100dvh-112px)] md:min-h-[calc(100dvh-48px)] flex items-center justify-center bg-surface-0">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
      </div>
    );
  }

  if (pageError || !group) {
    return (
      <div className="min-h-[calc(100dvh-112px)] md:min-h-[calc(100dvh-48px)] flex flex-col items-center justify-center bg-surface-0 px-4">
        <p className="text-sm text-status-busy mb-2">{pageError || 'Group not found.'}</p>
        <button
          type="button"
          onClick={() => router.push('/groups')}
          className="text-xs text-accent hover:text-accent-hover"
        >
          Back to Groups
        </button>
      </div>
    );
  }

  // Request-privacy non-members get the read-only request screen (rules +
  // request panel); chat stays locked behind approval.
  if (group.privacy === 'request' && !group.isJoined) {
    return (
      <RequestToJoinPanel
        group={{
          id: group.id,
          name: group.name,
          description: group.description,
          memberCount: group.memberCount,
          privacy: group.privacy,
          banner: group.banner ?? 'default',
          avatar_url: group.avatar_url ?? null,
          tags: group.tags,
          rules: group.rules ?? null,
        }}
        initialStatus={
          (group.viewerRequestStatus as 'none' | 'pending' | 'approved' | 'denied') ?? 'none'
        }
      />
    );
  }

  const ownerName = resolveDisplayName(viewerProfile as any, 'You');
  const ownerAvatar =
    (user?.user_metadata as Record<string, unknown> | undefined)?.avatar_url as string | undefined;

  return (
    <div className="bg-surface-0 text-text-primary">
      <div className="flex flex-col h-[calc(100dvh-112px)] md:h-[calc(100dvh-48px)]">
        <GroupHeader
          id={group.id}
          name={group.name}
          description={group.description}
          memberCount={group.memberCount}
          onlineCount={onlineIds.size}
          privacy={group.privacy}
          banner={group.banner ?? 'default'}
          avatarUrl={group.avatar_url ?? null}
          canManage={canManage}
          onOpenSettings={() => setShowSettings(true)}
        />

        {/* Mobile channel chip strip — single channel in Phase 1, but the strip
            renders for visual consistency with the spec. Hidden on md+. */}
        <div className="md:hidden border-b border-border-subtle bg-surface-1 overflow-x-auto">
          <div className="flex gap-1 px-3 py-2 whitespace-nowrap">
            {channels.map((c) => {
              const active = c.id === activeChannelId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveChannelId(c.id)}
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-xs',
                    active
                      ? 'bg-accent-soft text-accent'
                      : 'bg-surface-2 text-text-secondary',
                  )}
                >
                  <Hash className="w-3 h-3" strokeWidth={1.75} />
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[210px_1fr] lg:grid-cols-[210px_1fr_268px]">
          {/* Channels rail — md+ */}
          <aside className="hidden md:block min-h-0 overflow-hidden">
            <ChannelsPanel
              channels={channels}
              activeChannelId={activeChannelId}
              onSelect={setActiveChannelId}
              selfName={ownerName}
              selfAvatarUrl={ownerAvatar}
            />
          </aside>

          {/* Chat column */}
          <section className="flex flex-col min-h-0 min-w-0">
            <div className="h-11 flex items-center gap-2 px-4 border-b border-border-subtle bg-surface-1 flex-shrink-0">
              <Hash className="w-4 h-4 text-text-secondary" strokeWidth={1.75} />
              <span className="text-sm font-semibold text-text-primary">
                {activeChannel?.name || 'general'}
              </span>
            </div>

            <div
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto bg-surface-0 px-3 sm:px-5 pt-3 pb-2"
            >
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-text-tertiary">
                  No messages yet. Start the conversation.
                </div>
              ) : (
                <div className="space-y-1">
                  {messages.map((m, i) => {
                    const prev = messages[i - 1];
                    const showDay =
                      !prev || !isSameDay(new Date(prev.createdAt), new Date(m.createdAt));
                    const firstOfRun =
                      !prev ||
                      prev.userId !== m.userId ||
                      !isSameDay(new Date(prev.createdAt), new Date(m.createdAt));
                    const isHidden = !!(m.isDeleted || m.hidden);
                    const authorRole = roleByUserId.get(m.userId) ?? null;

                    // Hidden messages are server-filtered for regular members;
                    // only mods receive them. Defensive guard regardless.
                    if (isHidden && !isModerator) return null;

                    return (
                      <div key={m.id}>
                        {showDay && <DaySeparator iso={m.createdAt} />}
                        {isHidden ? (
                          <HiddenPlaceholder
                            username={m.user.username}
                            canUnhide={isModerator && canHideMessage(viewerRole, authorRole)}
                            onShow={() => void handleHideMessage(m.id, false)}
                          />
                        ) : (
                          <div className="group/msg relative flex gap-3 hover:bg-surface-1/50 rounded-sm px-1.5 py-0.5">
                            <div className="w-9 flex-shrink-0">
                              {firstOfRun && (
                                <Avatar
                                  src={m.user.avatar || undefined}
                                  name={m.user.name}
                                  size={36}
                                  username={m.user.username?.replace(/^@/, '') || null}
                                />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              {firstOfRun && (
                                <div className="flex items-baseline gap-2">
                                  <span className="text-sm font-semibold text-text-primary truncate">
                                    {m.user.name}
                                  </span>
                                  <span className="text-[11px] font-mono text-text-tertiary">
                                    {formatTimeOfDay(m.createdAt)}
                                  </span>
                                </div>
                              )}
                              {m.messageType === 'image' && m.mediaUrl ? (
                                <a href={m.mediaUrl} target="_blank" rel="noreferrer">
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={m.mediaUrl}
                                    alt={m.content || 'image attachment'}
                                    className="max-w-full max-h-[360px] rounded-md mt-1 object-cover"
                                    loading="lazy"
                                  />
                                </a>
                              ) : m.messageType === 'video' && m.mediaUrl ? (
                                <video
                                  src={m.mediaUrl}
                                  controls
                                  preload="metadata"
                                  className="max-w-full max-h-[360px] rounded-md mt-1 bg-surface-0"
                                />
                              ) : null}
                              {m.content && (
                                <p className="text-sm text-text-primary whitespace-pre-wrap break-words">
                                  {m.content}
                                </p>
                              )}
                            </div>

                            {/* Mod toolbar — hover, mods only, must outrank author. */}
                            {isModerator && canHideMessage(viewerRole, authorRole) && (
                              <div className="absolute top-0 right-1 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 transition-opacity">
                                <button
                                  type="button"
                                  onClick={() => void handleHideMessage(m.id, true)}
                                  aria-label="Hide message"
                                  title="Hide message"
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-sm bg-surface-2 border border-border-default text-text-tertiary hover:text-status-busy hover:bg-surface-3"
                                >
                                  <EyeOff className="w-3.5 h-3.5" strokeWidth={1.75} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {group.isJoined ? (
              <GroupComposer
                groupId={groupId}
                channelName={activeChannel?.name || 'general'}
                userId={user?.id || ''}
                onSend={handleSend}
              />
            ) : (
              <div className="border-t border-border-subtle bg-surface-1 p-4 text-center">
                <p className="text-xs text-text-secondary">
                  Join this group to send messages.
                </p>
              </div>
            )}
          </section>

          {/* Roster rail — lg+. The moderation surface. */}
          <aside className="hidden lg:block min-h-0 overflow-hidden">
            <RosterRail
              members={rosterMembers}
              viewerId={user?.id || ''}
              viewerRole={viewerRole}
              ownerId={group.ownerId}
              onlineIds={onlineIds}
              onChangeRole={handleChangeRole}
              onMute={handleMute}
              onKick={handleKick}
              onBan={handleBan}
            />
          </aside>
        </div>
      </div>

      {showSettings && (
        <GroupSettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          // ApiGroup is structurally wider than the modal's Group prop.
          group={group as unknown as Group}
          viewerRole={viewerRole}
          onSaved={(updated) => {
            // Sync from the PATCH response (not an optimistic guess). The spread
            // only overwrites keys present in the response, so viewerRole and
            // other page-only fields are preserved.
            setGroup((prev) =>
              prev ? ({ ...prev, ...(updated as Partial<ApiGroup>) } as ApiGroup) : prev,
            );
            setShowSettings(false);
          }}
          onDeleted={() => {
            setShowSettings(false);
            router.push('/groups');
          }}
        />
      )}
    </div>
  );
}

function HiddenPlaceholder({
  username,
  canUnhide,
  onShow,
}: {
  username: string;
  canUnhide: boolean;
  onShow: () => void;
}) {
  const handle = username?.startsWith('@') ? username : `@${username}`;
  return (
    <div className="flex items-center gap-2 px-3 py-2 my-0.5 rounded-sm border border-dashed border-border-default bg-surface-1/40">
      <EyeOff className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" strokeWidth={1.75} />
      <span className="text-xs text-text-tertiary italic flex-1 min-w-0 truncate">
        Message from {handle} removed by moderator
      </span>
      {canUnhide && (
        <button
          type="button"
          onClick={onShow}
          className="inline-flex items-center gap-1 text-[11px] text-accent hover:text-accent-hover flex-shrink-0"
        >
          <Eye className="w-3 h-3" strokeWidth={1.75} />
          Show
        </button>
      )}
    </div>
  );
}

export default function GroupDetailPage() {
  return (
    <AuthGuard>
      <GroupDetailInner />
    </AuthGuard>
  );
}
